import { Logs } from './Logs.js';

export class Notification {

    static parentSelector = null;
    static containerSelector = null;
    static images = null;
    static registry = new Map();

    constructor(options = {}) {
        this.options = {
            type: "classic",
            delay: 5000,
            ...options
        };

        this.id = this.options.id || Notification.createId();

        Notification.registry.set(this.id, this);
        this.render();

        return this;
    }

    static init(options) {
        if (!options || !options.parentSelector || !options.containerSelector || !options.images) {
            throw new Error("Notification.init requires parentSelector, containerSelector and images options");
        }

        Notification.parentSelector = options.parentSelector;
        Notification.containerSelector = options.containerSelector;
        Notification.images = options.images;
        Notification.build();
    }

    static build() {
        const parent = document.querySelector(Notification.parentSelector);
        if (!parent) {
            new Logs("error", [`Parent ${Notification.parentSelector} non trouvé, container de notifications impossible à créer`]);
            return false;
        }

        document.querySelector(Notification.containerSelector)?.remove();

        const container = document.createElement("div");
        if (Notification.containerSelector.startsWith("#")) {
            container.id = Notification.containerSelector.slice(1);
        } else if (Notification.containerSelector.startsWith(".")) {
            container.classList.add(...Notification.parseClasses(Notification.containerSelector.slice(1).replaceAll(".", " ")));
        }

        parent.append(container);
        return true;
    }

    static createId() {
        if (globalThis.crypto && typeof globalThis.crypto.randomUUID === "function") {
            return "not_" + globalThis.crypto.randomUUID().replaceAll("-", "").slice(0, 12);
        }

        return "not_" + Math.random().toString(36).slice(2, 14);
    }

    static normalizeId(notification) {
        if (notification && typeof notification === "object" && notification.id) {
            return notification.id;
        }

        return notification;
    }

    static setContent(notification, options) {
        const id = Notification.normalizeId(notification);
        const instance = Notification.registry.get(id);

        if (instance) {
            instance.setContent(options);
            return true;
        }

        return Notification.renderContent(id, options);
    }

    static remove(notification, delay) {
        const id = Notification.normalizeId(notification);
        const instance = Notification.registry.get(id);

        if (instance) {
            instance.remove(delay);
            return true;
        }

        return Notification.removeElement(id, delay);
    }

    static show(notification) {
        const id = Notification.normalizeId(notification);
        setTimeout(() => {
            document.getElementById(id)?.classList.add("visible");
        }, 100);
    }

    static hide(notification) {
        const id = Notification.normalizeId(notification);
        const element = document.getElementById(id);
        const modal = element?.closest(".ui-modal");

        element?.classList.remove("visible");

        if (element?.classList.contains("ui-modal-notification") && modal) {
            modal.classList.remove("unblured");
        }
    }

    static renderContent(notification, options = {}) {
        const id = Notification.normalizeId(notification);
        const element = document.getElementById(id);

        if (!element) {
            new Logs("error", [`Notification ${id} non trouvée, impossible d'assigner le contenu`]);
            return false;
        }

        const data = {
            delay: 5000,
            ...options
        };
        const isModalNotification = element.classList.contains("ui-modal-notification");
        const iconClass = isModalNotification ? "ui-modal-notification-icon" : "ui-not-icon";
        const titleClass = isModalNotification ? "ui-modal-notification-title" : "ui-not-title";
        const contentClass = isModalNotification ? "ui-modal-notification-content" : "ui-not-content";
        const buttonsClass = isModalNotification ? "ui-modal-notification-buttons" : "ui-not-buttons";

        if (Notification.mustPreserveIconColors(data.icon)) {
            element.classList.add("no-icon-filter");
        }

        element.replaceChildren();

        if (data.close) {
            const close = document.createElement("div");
            close.classList.add("ui-not-close");
            close.addEventListener("click", () => {
                Notification.remove(id, "now");
            });
            element.append(close);
        }

        if (data.icon) {
            const icon = document.createElement("div");
            icon.classList.add(...iconClass.split(" ").filter(Boolean));

            const image = document.createElement("img");
            image.src = Notification.getImageUrl(data.icon);
            icon.append(image);
            element.append(icon);
        }

        if (data.title) {
            const title = document.createElement("div");
            title.classList.add(titleClass);
            title.innerHTML = data.title;
            element.append(title);
        }

        if (data.content) {
            const content = document.createElement("div");
            content.classList.add(contentClass);
            content.innerHTML = data.content;
            element.append(content);
        }

        if (data.buttons) {
            const buttons = document.createElement("div");
            buttons.classList.add(buttonsClass);
            element.append(buttons);

            data.buttons.forEach((btn) => {
                const button = document.createElement("button");
                button.type = "button";
                button.id = Notification.createId();
                button.textContent = btn.label;

                if (btn.class) {
                    button.classList.add(...btn.class.split(" ").filter(Boolean));
                }

                button.addEventListener("click", () => {
                    console.log(`Clic on bouton ${btn.label}`);
                    btn.fct();
                });

                buttons.append(button);
            });
        }

        if (!data.wait) Notification.remove(id, data.delay);

        return true;
    }

    static removeElement(notification, delay) {
        const id = Notification.normalizeId(notification);

        if (!document.getElementById(id)) {
            new Logs("error", [`Notification ${id} non trouvée, impossible de la supprimer`]);
            return false;
        }

        const removeDelay = delay === "now" ? 1 : delay;

        setTimeout(() => {
            Notification.hide(id);

            setTimeout(() => {
                document.getElementById(id)?.remove();
                Notification.registry.delete(id);
            }, 500);
        }, removeDelay);

        return true;
    }

    static getImageUrl(icon) {
        if (Notification.images) {
            return `${Notification.images}/${icon}`;
        }

        return icon;
    }

    static parseClasses(classes) {
        if (!classes) return [];
        return String(classes).split(" ").filter(Boolean);
    }

    static mustPreserveIconColors(icon) {
        const filename = String(icon || "")
            .split(/[?#]/, 1)[0]
            .split("/")
            .pop();

        return ["check.svg", "warning.svg"].includes(filename);
    }

    render() {
        const type = this.options.type;

        if (type === "modal") {
            this.renderModal();
            return;
        }

        this.renderClassic(type);
    }

    renderClassic(type = "classic") {
        if (!document.querySelector(Notification.containerSelector)) {
            new Logs("error", [`Container ${Notification.containerSelector} non trouvé, notification impossible à afficher`]);
            return false;
        }

        const classes = ["ui-not"];
        if (this.options.class) classes.push(...Notification.parseClasses(this.options.class));
        if (type === "fullscreen") classes.push("ui-not-fullscreen");

        const notification = document.createElement("div");
        notification.id = this.id;
        notification.classList.add(...classes);
        document.querySelector(Notification.containerSelector).append(notification);

        this.setContent(this.options);
        this.show();

        return true;
    }

    renderModal() {
        if (!this.options.id_modal) {
            new Logs("error", ["Option id_modal requise pour une notification de type modal"]);
            return false;
        }

        const modal = document.getElementById(this.options.id_modal);
        if (!modal) {
            new Logs("error", [`Modal ${this.options.id_modal} non trouvée`]);
            return false;
        }

        modal.querySelectorAll(".ui-modal-notification").forEach((item) => {
            Notification.registry.delete(item.id);
            item.remove();
        });

        document.getElementById(this.id)?.remove();

        const notification = document.createElement("div");
        notification.id = this.id;
        notification.classList.add("ui-modal-notification");

        if (this.options.class) {
            notification.classList.add(...Notification.parseClasses(this.options.class));
        }

        modal.append(notification);
        modal.classList.add("unblured");

        this.setContent({
            ...this.options,
            wait: true
        });

        this.show();

        return true;
    }

    renderNative() {
        this.renderClassic();
    }

    setContent(options) {
        this.options = {
            ...this.options,
            ...options
        };

        return Notification.renderContent(this.id, this.options);
    }

    remove(delay) {
        return Notification.removeElement(this.id, delay);
    }

    show() {
        Notification.show(this.id);
    }

    hide() {
        Notification.hide(this.id);
    }

    toString() {
        return this.id;
    }

}

globalThis.Notification = Notification;
